import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, RowsSkeletonLoader } from '@node-space/ui-components'
import { useCustomerFeeWalletsQuery } from 'hooks/queries/Customers/useCustomerFeeWalletsQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { customerActionAtom } from '../atoms/customersAtoms'
import { AddFeeWalletForm } from './AddFeeWalletForm'

export const AssignCustomerFeeWallets = () => {
  const { t } = useTranslation()
  const customerAction = useAtomValue(customerActionAtom)

  const { data: fiatWalletsData, isFetching: isFetchingFiatWallets } = useWalletsQueryV2({
    walletType: 'fiat',
    max: 500,
  })

  const { data: cryptoWalletsData, isFetching: isFetchingCryptoWallets } = useWalletsQueryV2({
    walletType: 'crypto',
    max: 500,
  })

  const { data: feeWalletsData, isFetching: isFetchingFeeWallets } = useCustomerFeeWalletsQuery()

  const allWallets = [...(fiatWalletsData?.data || []), ...(cryptoWalletsData?.data || [])]

  const isFetchingAllWallets =
    isFetchingFiatWallets || isFetchingCryptoWallets || isFetchingFeeWallets

  const currencyOptions = useMemo(() => {
    if (!allWallets) return []

    const currencyList = allWallets
      ?.map(wallet => wallet.currency.code)
      .reduce((currencies: string[], currency: string) => {
        if (!currencies.includes(currency)) {
          currencies.push(currency)
        }
        return currencies
      }, [])
      .sort()

    return currencyList
  }, [allWallets])

  const assignedFeeWallets = useMemo(() => {
    if (!feeWalletsData?.content || !allWallets) return {}

    const feeWallets: Record<string, string> = {}

    feeWalletsData?.content?.forEach(feeWallet => {
      const matchedWallet = allWallets.find(
        wallet => wallet?.lsid === feeWallet?.destinationWalletId
      )
      if (matchedWallet) {
        feeWallets[feeWallet?.currency] = matchedWallet.id.toString()
      }
    })

    return feeWallets
  }, [feeWalletsData?.content, allWallets])

  const handleClose = () => {
    customerAction?.onClose?.()
  }

  return (
    <Box flexCol gapY={24} width="full" padding={24}>
      <Callout variant="info">{t('customers.feeSetup.walletSelectionDescription')}</Callout>

      <Box flexCol gapY={16} width="full">
        {isFetchingAllWallets && <RowsSkeletonLoader rows={currencyOptions?.length || 3} />}

        {!isFetchingAllWallets &&
          currencyOptions?.map(currency => {
            const selectedWalletReference = assignedFeeWallets[currency]
            const isEdit = !!selectedWalletReference

            return (
              <AddFeeWalletForm
                key={currency}
                currency={currency}
                allWallets={allWallets}
                isFetchingAllWallets={isFetchingAllWallets}
                selectedWalletReference={selectedWalletReference}
                isEdit={isEdit}
              />
            )
          })}
      </Box>

      <Box flex width="full" justify="between" paddingT={20}>
        <Button
          className="w-20"
          variant="secondary"
          onClick={handleClose}
          disabled={isFetchingAllWallets}
        >
          {t('cancel')}
        </Button>
        <Button
          className="w-20"
          variant="primary"
          onClick={handleClose}
          disabled={isFetchingAllWallets}
        >
          {t('done')}
        </Button>
      </Box>
    </Box>
  )
}
